<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { Switch } from '@headlessui/vue'
import { SunIcon, MoonIcon } from '@heroicons/vue/24/outline'

// Set initial state that will be updated on mount
const isDarkMode = ref(false)

const updateTheme = (value) => {
    if (typeof document !== 'undefined') {
        if (value) {
            document.documentElement.classList.add('dark')
            if (typeof localStorage !== 'undefined') {
                localStorage.theme = 'dark'
            }
        } else {
            document.documentElement.classList.remove('dark')
            if (typeof localStorage !== 'undefined') {
                localStorage.theme = 'light'
            }
        }
    }
}

onMounted(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        isDarkMode.value =
            localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)

        updateTheme(isDarkMode.value)

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const handleSystemThemeChange = (e) => {
            if (!('theme' in localStorage)) {
                isDarkMode.value = e.matches
                updateTheme(isDarkMode.value)
            }
        }
        mediaQuery.addEventListener('change', handleSystemThemeChange)

        onUnmounted(() => {
            mediaQuery.removeEventListener('change', handleSystemThemeChange)
        })
    }
})

watch(isDarkMode, (value) => {
    updateTheme(value)
})
</script>

<template>
    <div class="flex items-center justify-center">
        <Switch
            v-model="isDarkMode"
            :class="[
                isDarkMode ? 'bg-neutral-800' : 'bg-yellow-500',
                'relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
            ]"
        >
            <span class="sr-only">Use setting</span>

            <span
                :class="[
                    isDarkMode ? 'translate-x-7' : 'translate-x-0',
                    'pointer-events-none relative inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                ]"
            >
                <span
                    aria-hidden="true"
                    :class="[
                        isDarkMode ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in',
                        'absolute inset-0 flex size-full items-center justify-center transition-opacity',
                    ]"
                >
                    <SunIcon class="size-5 text-yellow-500" />
                </span>

                <span
                    aria-hidden="true"
                    :class="[
                        isDarkMode ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out',
                        'absolute inset-0 flex size-full items-center justify-center transition-opacity',
                    ]"
                >
                    <MoonIcon class="size-5 text-indigo-600" />
                </span>
            </span>
        </Switch>
    </div>
</template>
